import { useState } from 'react'
import { useAudio, PLAYBACK_RATES } from '../contexts/AudioContext'
import { QueueDrawer } from './QueueDrawer'

export function MiniPlayer() {
  const {
    currentItem,
    queue,
    isPlaying,
    isLoading,
    currentTime,
    duration,
    playbackRate,
    toggle,
    skipForward,
    skipBackward,
    setPlaybackRate,
    stop,
    play
  } = useAudio()

  const [expanded, setExpanded] = useState(false)
  const [showQueue, setShowQueue] = useState(false)

  // Don't show if nothing playing and queue empty
  if (!currentItem && queue.length === 0) return null

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (expanded) {
    return (
      <div className="expanded-player">
        <button className="collapse-btn" onClick={() => setExpanded(false)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
          Minimize
        </button>

        <div className="player-artwork">
          <div className="artwork-placeholder">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          </div>
        </div>

        <div className="player-info">
          <h2>{currentItem?.title || 'No track'}</h2>
          <p>{currentItem?.source || 'Queue ready'}</p>
        </div>

        <div className="player-progress">
          <input
            type="range"
            min="0"
            max={duration || 100}
            value={currentTime}
            onChange={() => {/* seek would go here */}}
            className="progress-slider"
          />
          <div className="time-labels">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        <div className="player-controls">
          <button onClick={() => skipBackward(15)} className="skip-btn" title="Back 15s">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z"/>
            </svg>
            <span className="skip-label">15</span>
          </button>

          <button onClick={toggle} className="play-btn large" disabled={isLoading && !currentItem}>
            {isLoading ? (
              <span className="spinner" />
            ) : isPlaying ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>

          <button onClick={() => skipForward(15)} className="skip-btn" title="Forward 15s">
            <span className="skip-label">15</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z"/>
            </svg>
          </button>
        </div>

        <div className="player-extras">
          <div className="speed-control">
            <label>Speed</label>
            <select
              value={playbackRate}
              onChange={(e) => setPlaybackRate(Number(e.target.value))}
            >
              {PLAYBACK_RATES.map(rate => (
                <option key={rate} value={rate}>{rate}x</option>
              ))}
            </select>
          </div>

          <button className="queue-btn" onClick={() => setShowQueue(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
            </svg>
            Queue ({queue.length})
          </button>

          <button className="stop-btn" onClick={stop}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="1" />
            </svg>
            Stop
          </button>
        </div>

        {showQueue && <QueueDrawer onClose={() => setShowQueue(false)} />}
      </div>
    )
  }

  // Mini player (collapsed)
  return (
    <>
      <div className="mini-player" onClick={() => setExpanded(true)}>
        <div className="mini-progress" style={{ width: `${progress}%` }} />

        <div className="mini-content">
          <div className="mini-info">
            <span className="mini-title">{currentItem?.title || 'Queue ready'}</span>
            <span className="mini-source">{currentItem?.source || `${queue.length} in queue`}</span>
          </div>

          <div className="mini-controls" onClick={e => e.stopPropagation()}>
            <button onClick={() => skipBackward(15)} title="Back 15s">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z"/>
              </svg>
            </button>
            <button
              onClick={currentItem ? toggle : play}
              className="mini-play"
              disabled={isLoading && !currentItem}
            >
              {isLoading ? (
                <span className="spinner-small" />
              ) : isPlaying ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>
            <button onClick={() => skipForward(15)} title="Forward 15s">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {showQueue && <QueueDrawer onClose={() => setShowQueue(false)} />}
    </>
  )
}
